import React, { useCallback, useContext } from 'react'

import CornerControls from './common/CornerControls'
import DetailCardLayout from './common/DetailCardLayout'
import ItemCardImage from './common/ItemCardImage'
import ItemTitle from './common/ItemTitle'
import NotesAndAttachments from './common/NotesAndAttachments'
import PrintOnlyAddressAndNotes from './common/PrintOnlyAddressAndNotes'
import { DetailItemCardVariant } from './common/types'
import HotelPriceAndCTAs from './HotelPriceAndCTAs'
import HotelUrgencyLabels from './HotelUrgencyLabels'

import NumberRating from 'components/Common/NumberRating/NumberRating'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LineClockIcon from 'components/Luxkit/Icons/line/LineClockIcon'
import LineNotesIcon from 'components/Luxkit/Icons/line/LineNotesIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { nonNullable } from 'lib/array/arrayUtils'
import { showUserReviewsRating } from 'lib/order/reviewUtils'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useOffer } from 'tripPlanner/hooks/api'
import useAccommodationItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useAccommodationItemOccupancyDescription'
import {
  HotelItem,
  HomeAndVillasItem,
  AirbnbItem,
  AccommodationItem,
} from 'tripPlanner/types/tripItem'
import {
  formatShortDateRange,
  getOfferImage,
  isCustomItem,
  isLeBookmark,
} from 'tripPlanner/utils'
import OutsideTripDatesIcon from '../Itinerary/OutsideTripDatesIcon'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import EditTripItem from '../TripModal/TripItems/Edit'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { itemSectionId } from 'tripPlanner/utils/itemSection'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useNavigateTo from 'hooks/useNavigateTo'
import Group from 'components/utils/Group'
import { NarrowCardOnlyBookedLabel } from './common/BookedLabel'

interface Props {
  item: HotelItem | HomeAndVillasItem | AirbnbItem | AccommodationItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailAccommodationCard({ item, variant, tripNameLabel }: Props) {
  const offerId = 'code' in item ? item.code : undefined
  // The query type includes many offer types, but this card is only used for hotel/villa offers
  const { data: _offer } = useOffer(offerId)
  const offer = _offer && _offer.parentType === 'hotel' ? _offer : undefined

  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const viewAsTemplate = !!tripContext?.viewAsTemplate
  const timeFormat = useRegionTimeFormat()
  const offerLink = isLeBookmark(item) ? buildProductLink(item, !viewAsTemplate, { offer }) : undefined

  const { id: imgId, url: imgSrc } = getOfferImage(offer)

  const timeDescription = formatShortDateRange(item)
  const { occupanciesText } = useAccommodationItemOccupancyDescription(item)
  const timeAndOccupancyText = nonNullable([timeDescription, occupanciesText])
    .filter((text) => text.length > 0)
    .join(', ')

  const placeId = 'id' in item.place ? item.place.id : undefined
  let rating: number | undefined
  let numberOfReviews: number | undefined

  if (isLeBookmark(item) && offer) {
    if ('property' in offer && offer.property) {
      rating = offer.property.rating
      numberOfReviews = offer.property.reviewsTotal
    }
  }

  let checkInText: string | undefined = undefined
  if (item.startTime) {
    checkInText = isLeBookmark(item) ?
      `Check-in from ${item.startTime.format(timeFormat)}` :
      `Check-in at ${item.startTime.format(timeFormat)}`
  }

  const openModal = useOpenModal()
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const navigateTo = useNavigateTo()

  const onClickAddTripItemDates = useCallback(() => {
    if (isCustomItem(item)) {
      openModal(EditTripItem, { item })
    } else if (offerLink) {
      navigateTo(offerLink + '#date-picker')
      setImmersiveMode({ tripId: item.tripId, sectionId: itemSectionId(item.id) })
    }
  }, [item, offerLink, openModal, navigateTo, setImmersiveMode])

  const dateWarningIcon =
  variant === 'tripSummary' ?
    <OutsideTripDatesIcon
      item={item}
      changeItemDates={onClickAddTripItemDates}
    /> :
    undefined

  const showRating = showUserReviewsRating(rating, numberOfReviews)

  return (
    <DetailCardLayout data-testid="detail-card-accommodation">
      <DetailCardLayout.Image>
        <ItemCardImage
          item={item}
          name={item.place.name}
          imgId={imgId}
          imgSrc={imgSrc}
          placeId={placeId}
        />
      </DetailCardLayout.Image>
      <DetailCardLayout.Content>
        {canEdit && <CornerControls item={item} variant={variant} />}
        {!showRating && item.isBooked && <NarrowCardOnlyBookedLabel className="bottom-margin" />}
        <VerticalSpacer gap={8}>
          {showRating && <Group direction="horizontal" gap={8} noDisplayWhenEmpty>
            <NumberRating rating={rating!} hideLink variant="xs" onlyNumber />
            {item.isBooked && <NarrowCardOnlyBookedLabel />}
          </Group>}
          <VerticalSpacer gap={4}>
            <BodyText variant="small" colour="neutral-two">
              {item.place.region}
            </BodyText>
            <ItemTitle icon={LineBedIcon}>{item.place.name}</ItemTitle>
            {checkInText && variant === 'itinerary' && (
              <BodyTextBlock
                variant="medium"
                colour="neutral-three"
                startIcon={<LineClockIcon />}
              >
                {checkInText}
              </BodyTextBlock>
            )}
          </VerticalSpacer>
          {(item.type === 'HOTEL' ||
            item.type === 'ACCOMMODATION_RENTAL' ||
            item.type === 'ACCOMMODATION') &&
            offer && (
              <HotelUrgencyLabels
                item={item}
                offer={offer}
                viewAsTemplate={viewAsTemplate}
              />
          )}
          {timeAndOccupancyText && (
            <BodyTextBlock variant="medium" startIcon={<LineNotesIcon />} endIcon={dateWarningIcon}>
              {timeAndOccupancyText}
            </BodyTextBlock>
          )}
          {tripNameLabel}
          <NotesAndAttachments
            item={item}
            canEdit={canEdit}
            variant={variant}
          />
          {(item.type === 'HOTEL' ||
            item.type === 'ACCOMMODATION_RENTAL' ||
            item.type === 'ACCOMMODATION') &&
            offer &&
            variant !== 'itinerary' && (
              <HotelPriceAndCTAs
                item={item}
                offer={offer}
                viewAsTemplate={viewAsTemplate}
                variant={variant}
              />
          )}
          <PrintOnlyAddressAndNotes item={item} />
        </VerticalSpacer>
      </DetailCardLayout.Content>
    </DetailCardLayout>
  )
}

export default DetailAccommodationCard
